import Constants from 'expo-constants';

import {
  ApplePayValidationDocument,
  CartPaymentCardType,
  IApplePayValidationQuery,
  IApplePayValidationQueryVariables,
  PaymentProcessor,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';

import { PipelineBaseStep } from '../core/pipeline-base-step';
import type {
  ApplePayContext,
  ApplePayDetails,
  IAppleConfig,
  PipelineStepContext,
  PipelineStepResult,
} from '../payment-types';

interface PaymentConfig {
  region: string;
  currencyCode: string;
  totalAmount: string;
  merchantId: string;
  merchantName: string;
}

interface BillingAddress {
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
}

/**
 * Web Version Apple Pay Step
 * 1. Validates configuration and pipeline context
 * 2. Builds and executes payment requests (Worldpay or Orbital)
 * 3. Parses and transforms payment responses
 * 4. Returns payment details for order commit
 *
 * Uses Apple Pay JS API (window.ApplePaySession)
 * Only works on Safari with Apple Pay enabled
 *
 */
export class ApplePayStep extends PipelineBaseStep {
  readonly name = 'ApplePayStep';
  private region: 'US' | 'CA';

  constructor() {
    super();
    // Backend requires country to be CountryCode (e.g. 'US' | 'CA'). buildRegion may be undefined or lowercase.
    const buildRegion = Constants?.expoConfig?.extra?.eas?.buildRegion;
    this.region = buildRegion === 'CA' ? 'CA' : 'US';
  }

  async execute(pipelineContext: PipelineStepContext): Promise<PipelineStepResult> {
    if (pipelineContext.paymentMethod !== 'applePay') {
      return this.success();
    }

    // Step 1: Obtain Apple config from expo constants
    // if it has Apple details already from a retry step continue
    if (pipelineContext.applePayDetails) {
      this.logger.log('[ApplePay] Payment details already available', {
        paymentType: pipelineContext.applePayDetails.PaymentMethodData.paymentType,
      });
      return this.success();
    }

    const appleConfig = this.getConfiguration(pipelineContext);
    if (!appleConfig) {
      return this.error(
        'APPLE_PAY_CONFIG_MISSING',
        'Apple Pay configuration not found',
        'Apple Pay is not available at this time. Please use another payment method. Error Code: (AConfig Missing)',
        'ApplePayStep'
      );
    }

    // Step 2: Make sure the Apple Pay API is available
    const apiValidationResult = this.validateApplePayApi();
    if (apiValidationResult) {
      return apiValidationResult;
    }

    // Step 3: Build and check the payment config
    try {
      const paymentConfig = this.buildPaymentConfig(pipelineContext, appleConfig);

      const validationResult = this.validatePaymentConfig(paymentConfig);
      if (validationResult) {
        return validationResult;
      }

      const applePayDetails = await this.executePaymentRequest(pipelineContext, paymentConfig);

      // Step 4: Execute the request and assemble pipeline context for commit order
      return this.handlePaymentResponse(applePayDetails, pipelineContext);
    } catch (error) {
      return this.error(
        'APPLE_PAY_ERROR',
        error?.message || 'Unknown error',
        'Apple Pay payment did not complete. Please try again. Error Code: (Apple Pay Failed)',
        'ApplePayStep'
      );
    }
  }

  private getConfiguration(context?: PipelineStepContext): IAppleConfig | null {
    const appleConfig: IAppleConfig | undefined =
      context?.appleConfig ?? Constants?.expoConfig?.extra?.eas?.apple;

    if (!appleConfig) {
      this.logger.logError('[ApplePay] Apple Pay configuration not found');
      return null;
    }

    return appleConfig;
  }

  private validateApplePayApi(): PipelineStepResult | null {
    if (typeof window === 'undefined' || !window.ApplePaySession) {
      this.logger.logError('[ApplePay] Apple Pay API not available on web');
      return this.error(
        'APPLE_PAY_API_NOT_LOADED',
        'Apple Pay API not available',
        'Apple Pay is not available. Please use another payment method. Error Code: (AApi Not Loaded)'
      );
    }
    if (!ApplePaySession.canMakePayments()) {
      this.logger.logError('[ApplePay] Device cannot make Apple Pay payments');
      return this.error(
        'APPLE_PAY_NOT_SUPPORTED',
        'Apple Pay not supported on this device',
        'Apple Pay is not available on this device. Please use another payment method. Error Code: (AApi Not Supported)'
      );
    }

    return null;
  }

  private buildPaymentConfig(
    pipelineContext: PipelineStepContext,
    appleConfig: IAppleConfig
  ): PaymentConfig {
    const currencyCode = this.region === 'CA' ? 'CAD' : 'USD';
    const totalAmount = (pipelineContext.cartTotal / 100).toString();
    const merchantId = appleConfig.merchantId;

    return {
      region: this.region,
      currencyCode,
      totalAmount,
      merchantId,
      merchantName: 'Firehouse Subs',
    };
  }

  private validatePaymentConfig(config: PaymentConfig): PipelineStepResult | null {
    if (!config.merchantId) {
      this.logger.logError('[ApplePay] Missing merchantId in config');
      return this.error(
        'APPLE_PAY_CONFIG_MISSING',
        'Merchant ID not configured',
        'Apple Pay is not configured properly. Please use another payment method. Error Code: (AConfig MerchantId Missing)'
      );
    }

    return null;
  }

  private async executePaymentRequest(
    pipelineContext: PipelineStepContext,
    config: PaymentConfig
  ): Promise<ApplePayDetails> {
    this.logPaymentRequest(pipelineContext.processor, config);

    const paymentRequest = this.buildPaymentRequest(config, pipelineContext);

    return new Promise((resolve, reject) => {
      const session = new ApplePaySession(1, paymentRequest);

      session.onvalidatemerchant = async event => {
        try {
          this.logger.log('[ApplePay] Validating merchant', { validationURL: event.validationURL });

          const merchantSession = await this.validateMerchant(event.validationURL, pipelineContext);
          session.completeMerchantValidation(merchantSession);
        } catch (error) {
          this.logger.logError('[ApplePay] Merchant validation failed', error);
          session.abort();
          reject(new Error('Merchant validation failed'));
        }
      };

      session.onpaymentauthorized = event => {
        this.logger.log('[ApplePay] Payment authorized');

        const { payment } = event;
        const applePayDetails = this.parsePaymentResponse(payment, pipelineContext);

        // Note: Frontend also completes with SUCCESS immediately (chargeCreationSucceeds hardcoded true)
        // In pipeline architecture, order commit happens in CommitOrderStep, so we complete here
        // after successfully parsing the token, matching frontend behavior
        session.completePayment(ApplePaySession.STATUS_SUCCESS);

        resolve(applePayDetails);
      };

      session.oncancel = () => {
        this.logger.log('[ApplePay] User cancelled payment');
        reject(new Error('User cancelled Apple Pay'));
      };

      this.logger.log('[ApplePay] Opening Apple Pay sheet (Web)');

      session.begin();
    });
  }

  private buildPaymentRequest(
    config: PaymentConfig,
    context: PipelineStepContext
  ): ApplePayJS.ApplePayPaymentRequest {
    const countryCode = config.region === 'CA' ? 'CA' : 'US';
    const appleConfig = this.getConfiguration(context);
    const supportedNetworks = appleConfig.paymentsNetworks ?? [];

    return {
      countryCode,
      currencyCode: config.currencyCode,
      supportedNetworks,
      merchantCapabilities: ['supports3DS'],
      total: {
        label: config.merchantName,
        amount: config.totalAmount,
      },
      requiredBillingContactFields: ['postalAddress'],
    };
  }

  private async validateMerchant(
    validationURL: string,
    context: PipelineStepContext
  ): Promise<IApplePayValidationQuery['applePayValidation']> {
    const appleConfig = this.getConfiguration(context);
    const displayName = 'Firehouse Subs';

    this.logger.log('[ApplePay] Validating merchant with Apple', {
      validationURL,
      processor: context.processor,
      merchantId: appleConfig.merchantId,
      displayName,
    });

    try {
      const variables: IApplePayValidationQueryVariables = {
        merchantIdentifier: appleConfig.merchantId,
        displayName,
        validationUrl: validationURL,
        initiative: 'web',
        initiativeContext: typeof window !== 'undefined' ? window.location.host : '',
      };

      const result = await legacyClient.query<
        IApplePayValidationQuery,
        IApplePayValidationQueryVariables
      >({
        query: ApplePayValidationDocument,
        variables,
        fetchPolicy: 'no-cache',
      });

      if (!result?.data?.applePayValidation) {
        this.logger.logError(
          '[ApplePay] No merchant session returned from validation',
          result?.errors
        );
        throw new Error('Failed to obtain merchant session from Apple');
      }

      this.logger.log('[ApplePay] Merchant validation successful', {
        merchantSessionIdentifier: result.data.applePayValidation.merchantSessionIdentifier,
      });

      return result.data.applePayValidation;
    } catch (error) {
      this.logger.logError('[ApplePay] Merchant validation failed', error);
      throw error;
    }
  }

  private handlePaymentResponse(
    applePayDetails: ApplePayDetails,
    context: PipelineStepContext
  ): PipelineStepResult {
    // For Apple Pay, creditType must always be APPLE_PAY (not the underlying card type)
    // Frontend hardcodes: cardBrand: CartPaymentCardType.APPLE_PAY
    const creditType = CartPaymentCardType.APPLE_PAY;

    this.logger.log('[ApplePay] Payment successful', {
      processor: context.processor,
      paymentType: applePayDetails.PaymentMethodData.paymentType,
      creditType,
    });

    return this.success({
      applePayDetails,
      creditType,
    } as Partial<ApplePayContext>);
  }

  private parsePaymentResponse(payment: ApplePayJS.ApplePayPayment, context: PipelineStepContext) {
    const { token, billingContact } = payment;
    const { paymentData, paymentMethod } = token;

    // Convert payment data to base64
    const paymentDataString = JSON.stringify(paymentData);
    const base64PaymentData = btoa(paymentDataString);

    const billingAddress = this.parseBillingAddress(billingContact);
    const formattedAddress = this.formatBillingAddress(billingAddress);

    // Extract payment token details
    const tokenData = paymentData as any;
    // typed as any in node_modules/@types/applepayjs/index.d.ts
    const { signature, version, data } = tokenData;
    const { ephemeralPublicKey, publicKeyHash, transactionId } = tokenData.header || {};

    // Match workspace/frontend: backend expects merchant id, not processor name (enable-digital-wallet-migration is false so use merchantId)
    const decryptAlias = context.appleConfig?.merchantId;

    return {
      signature: signature || '',
      data: data || base64PaymentData,
      version: version,
      ephemeralPublicKey: ephemeralPublicKey || '',
      publicKeyHash: publicKeyHash || '',
      transactionId: transactionId || '',
      decryptAlias,
      applicationData: '',
      country: this.region,
      formatted: formattedAddress,
      paymentData: paymentDataString,
      primary: true,
      type: 'home',
      billingAddress,
      PaymentMethodData: {
        paymentType: paymentMethod.network ?? '',
        displayName: paymentMethod.displayName,
      },
    };
  }

  private parseBillingAddress(billingContact?: ApplePayJS.ApplePayPaymentContact): BillingAddress {
    if (!billingContact) {
      return {
        locality: '',
        postalCode: '',
        region: '',
        streetAddress: '',
      };
    }

    const streetAddress = billingContact.addressLines?.[0] ?? '';
    // Sanitize postal code to remove spaces, dashes, and special characters (matches workspace/frontend)
    const rawPostalCode = billingContact.postalCode || '';
    const sanitizedPostalCode = rawPostalCode.replace(/[^A-Za-z0-9]/g, '');

    return {
      locality: billingContact.locality || '',
      postalCode: sanitizedPostalCode,
      region: billingContact.administrativeArea || '',
      streetAddress,
    };
  }

  private formatBillingAddress(address: BillingAddress): string {
    return [address.streetAddress, address.locality, address.region, address.postalCode]
      .filter(Boolean)
      .join(', ');
  }

  private logPaymentRequest(processor: PaymentProcessor, config: PaymentConfig): void {
    this.logger.log('[ApplePay] Requesting payment', {
      platform: 'web',
      processor,
      merchantId: config.merchantId,
      totalAmount: config.totalAmount,
      currencyCode: config.currencyCode,
      region: config.region,
    });
  }
}
